import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { combineReasons } from '@lib/prescriptions/data-access';
// OLD: from '../../../core/utils/prescription-reason.function';
import { PrescriptionsService } from '@lib/prescriptions/data-access';
// OLD: from '../../../core/services/prescriptions.service';
import { addPrescriptionDraft, updatePrescriptionDraft } from '@lib/prescriptions/data-access';
// OLD: from '../../../core/store/prescriptions-drafts.actions';
import { reasonNotValid } from '@lib/prescriptions/data-access';
// OLD: from '../../../core/validators/decline-reason.validator';
import { APPROVE_REASON, DECLINE_REASONS, EDIT_REASONS, GuaranteeStatus } from '@lib/prescriptions/data-access';
// OLD: from '../../../core/models/guarantee-application.interface';
import { PersonalDoctorControls, PersonalDoctorMetadata, Prescription, PrescriptionControls, PrescriptionItem, PrescriptionTypes } from '@lib/prescriptions/data-access';
// OLD: from '../../../core/models/prescription.interface';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { concatMap, forkJoin, map, merge, of, take, tap } from 'rxjs';

import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';

import { DoctorsService } from '@hm/core/services/doctors.service';

import { PersonalDoctor, PersonalDoctorAppointmentName } from '@lib/core/models/doctor.interface';
import { selectMemberProfile } from '@lib/members/data-access';

import { AchiCode } from '@lib/shared-ui/core/models/achi-codes.interface';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

const DOCTOR_MIN_PERIOD: number = 30;

@Component({
  selector: 'prescriptions-personal-doctor-prescription-form-modal',
  standalone: true,
  imports: [
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    MatInput,
    MatSuffix,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    LoaderComponent,
    MatCheckboxModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatError,
    MatHint,
    MatTooltip,
    TranslatePipe,
    NgClass,
  ],
  templateUrl: './personal-doctor-prescription-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonalDoctorPrescriptionFormModalComponent implements OnInit {
  private readonly data: {
    prescription: Prescription | null;
    prescriptionType: PrescriptionTypes;
    memberId: string;
    guaranteeApplicationId: string;
    pharmaciesCount: number;
  } = inject(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<PersonalDoctorPrescriptionFormModalComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly prescriptionsService = inject(PrescriptionsService);
  private readonly doctorsService = inject(DoctorsService);
  private readonly store = inject(Store);

  protected readonly prescriptionTypes = PrescriptionTypes;
  protected readonly guaranteeStatus = GuaranteeStatus;
  protected readonly personalDoctorAppointmentName = PersonalDoctorAppointmentName;
  protected readonly declineReasons: string[] = DECLINE_REASONS;
  protected readonly editReasons: string[] = EDIT_REASONS;
  protected readonly guaranteeStatusOptions: GuaranteeStatus[] = [this.guaranteeStatus.Guaranteed, this.guaranteeStatus.NotGuaranteed, this.guaranteeStatus.Auto];

  protected readonly personalDoctor = signal<Prescription>(this.data.prescription);
  protected readonly memberId = signal<string>(this.data.memberId);
  protected readonly guaranteeApplicationId = signal<string>(this.data.guaranteeApplicationId);
  protected readonly loading = signal<boolean>(true);
  protected readonly statusChanged = signal<boolean>(false);
  protected readonly dataChanged = signal<boolean>(false);
  protected readonly completionChecked = signal<boolean>(false);
  protected readonly personalDoctorsOptions = signal<PersonalDoctor[]>([]);
  protected readonly personalDoctorServiceOptions = signal<AchiCode[]>([]);
  protected readonly filteredDeclineReasons = signal<string[]>(this.declineReasons);
  protected readonly filteredEditReasons = signal<string[]>(this.editReasons);
  protected readonly franchiseInitialValue = signal<number>(0);

  protected personalDoctorForm: FormGroup<PrescriptionControls<PersonalDoctorControls>>;
  protected startDate = new Date();
  protected endDate = new Date(this.startDate);
  protected memberProfile = this.store.selectSignal(selectMemberProfile);

  protected get fc(): PrescriptionControls<PersonalDoctorControls> {
    return this.personalDoctorForm?.controls;
  }

  protected get metadataFc(): PersonalDoctorControls {
    return this.personalDoctorForm.controls.metadata.controls;
  }

  ngOnInit() {
    this.initPersonalDoctorAppointmentData();
  }

  private initPersonalDoctorForm() {
    this.personalDoctorForm = this.fb.group<PrescriptionControls<PersonalDoctorControls>>({
      type: this.fb.control<PrescriptionTypes | null>(PrescriptionTypes.PersonalDoctorAppointment),
      name: this.fb.control<string | null>(this.personalDoctorAppointmentName, [Validators.required]),
      startDate: this.fb.control<Date | null>(this.startDate, [Validators.required]),
      endDate: this.fb.control<Date | null>(this.getEndDate(), [Validators.required]),
      metadata: this.fb.group<PersonalDoctorControls>({
        serviceId: this.fb.control<string | null>(null, [Validators.required]),
        serviceName: this.fb.control<string | null>(null, [Validators.required]),
        doctorId: this.fb.control<string | null>(null, [Validators.required]),
        doctorName: this.fb.control<string | null>(null, [Validators.required]),
        doctorPartnerId: this.fb.control<string | null>(null),
      }),
      guaranteeStatus: this.fb.control<string | null>(null, [Validators.required]),
      editReason: this.fb.control<string | null>(null),
      statusReason: this.fb.control<string>(null),
      sendGuaranteeNotification: this.fb.control<boolean>(true),
      franchise: this.fb.control<number>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      franchiseManualChangeConfirmation: this.fb.control<boolean>(true, [Validators.requiredTrue]),
      completedInAppointment: this.fb.control<boolean>(false),
      originalPrescriptionId: this.fb.control<string | null>(null),
    });
    this.watchAllDataChanges();
    this.watchStatusChanges();
    this.watchCompletionCheckboxChanges();
    this.watchDeclineReasonsChanges();
    this.watchEditReasonsChanges();

    if (this.personalDoctor()) this.patchPersonalDoctorForm(this.personalDoctor());
    else {
      this.statusChanged.set(true);
      this.fc.statusReason.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
      this.fc.statusReason.updateValueAndValidity({ emitEvent: false });
    }
  }

  private patchPersonalDoctorForm(personalDoctor: any) {
    this.personalDoctorForm.patchValue(
      {
        type: personalDoctor.type,
        name: personalDoctor.name,
        startDate: new Date(personalDoctor.startDate),
        endDate: new Date(personalDoctor.endDate),
        metadata: {
          serviceId: personalDoctor.metadata.serviceId ?? personalDoctor.metadata.service_id,
          serviceName: personalDoctor.metadata.serviceName ?? personalDoctor.metadata.service_name,
          doctorId: personalDoctor.metadata.doctorId ?? personalDoctor.metadata.doctor_id,
          doctorName: personalDoctor.metadata.doctorName ?? personalDoctor.metadata.doctor_name,
          doctorPartnerId: personalDoctor.metadata.doctorPartnerId ?? personalDoctor.metadata.doctor_partner_id ?? null,
        },
        guaranteeStatus: personalDoctor.guaranteeStatus as string,
        franchise: personalDoctor.franchise || 0,
        originalPrescriptionId: personalDoctor.originalPrescriptionId,
      },
      { emitEvent: false }
    );
    this.franchiseInitialValue.set(personalDoctor.franchise || 0);

    if (this.fc.guaranteeStatus.value === this.guaranteeStatus.Auto || this.fc.guaranteeStatus.value === this.guaranteeStatus.Guaranteed) {
      this.fc.guaranteeStatus.disable();
    }
  }

  private initPersonalDoctorAppointmentData() {
    this.loading.set(true);
    this.initPersonalDoctorForm();

    return forkJoin([
      this.doctorsService.getPersonalDoctors(this.memberProfile().userId).pipe(take(1)),
      this.doctorsService.getPersonalDoctorServices(this.memberProfile().userId).pipe(take(1)),
    ]).subscribe({
      next: ([doctors, services]) => {
        this.personalDoctorsOptions.set(doctors.items);
        this.personalDoctorServiceOptions.set(services.items);

        // Only set default service if creating new prescription (not editing)
        if (!this.personalDoctor() && this.personalDoctorServiceOptions().length > 0) {
          this.fc.metadata.patchValue({
            serviceId: this.personalDoctorServiceOptions()[0].code,
            serviceName: this.personalDoctorServiceOptions()[0].nameUa,
          });
        }
        this.loading.set(false);
      },
      error: () => {
        this.personalDoctorsOptions.set([]);
        this.personalDoctorServiceOptions.set([]);
        this.loading.set(false);
      },
    });
  }

  protected selectPersonalDoctorAppointmentType(service: MatSelectChange) {
    const serviceCode: string = this.personalDoctorServiceOptions().find(item => item.nameUa === service.value || item.customName === service.value).code;
    this.metadataFc.serviceId.patchValue(serviceCode);
  }

  protected selectPersonalDoctor(doctor: MatSelectChange) {
    const selectedDoctor = this.personalDoctorsOptions().find(item => item.fullName === doctor.value);
    this.metadataFc.doctorId.patchValue(selectedDoctor.id);
    this.metadataFc.doctorPartnerId.patchValue(selectedDoctor?.partnerId ?? null);
  }

  private watchStartDateChanges() {
    return this.fc.startDate.valueChanges.pipe(
      tap(() => this.fc.endDate.patchValue(this.getEndDate(), { emitEvent: false })),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchEndDateChanges() {
    return this.fc.endDate.valueChanges.pipe(
      concatMap((value: Date) => {
        if (value) value.setHours(23, 59, 59, 999);

        return of(value);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchFranchiseChanges() {
    return this.fc.franchise.valueChanges.pipe(
      tap(value => this.fc.franchiseManualChangeConfirmation.setValue(value === this.franchiseInitialValue())),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchStatusChanges() {
    this.fc.guaranteeStatus.valueChanges
      .pipe(
        tap((value: string) => {
          const statusReasonControl = this.fc.statusReason;
          if (value === this.guaranteeStatus.Guaranteed) {
            statusReasonControl.setValue(APPROVE_REASON);
            statusReasonControl.setValidators([Validators.required]);
          } else {
            statusReasonControl.setValue(null);
            statusReasonControl.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
          }
          if (this.personalDoctor()) {
            const status = this.personalDoctor().guaranteeStatus ? this.personalDoctor().guaranteeStatus : this.personalDoctor().status;

            if (value !== status) this.statusChanged.set(true);
            else {
              this.statusChanged.set(false);
              statusReasonControl.clearValidators();
            }
          }
          statusReasonControl.updateValueAndValidity();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchDeclineReasonsChanges() {
    this.fc.statusReason.valueChanges
      .pipe(
        map(value => (value ? value : '')),
        tap(value => this.filteredDeclineReasons.set(this.declineReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchEditReasonsChanges() {
    this.fc.editReason.valueChanges
      .pipe(
        map(value => (value ? value : '')),
        tap(value => this.filteredEditReasons.set(this.editReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchCompletionCheckboxChanges() {
    return this.fc.completedInAppointment.valueChanges
      .pipe(
        tap(value => {
          this.completionChecked.set(!!value);

          if (value) this.personalDoctorForm.addControl('completionReason', this.fb.control(null, [Validators.required]));
          else this.personalDoctorForm.removeControl('completionReason');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchAllDataChanges() {
    merge(
      this.fc.metadata.controls.serviceId.valueChanges,
      this.fc.metadata.controls.serviceName.valueChanges,
      this.fc.metadata.controls.doctorId.valueChanges,
      this.fc.metadata.controls.doctorName.valueChanges,
      this.watchStartDateChanges(),
      this.watchEndDateChanges(),
      this.watchFranchiseChanges()
    )
      .pipe(
        tap(() => {
          if (this.personalDoctor()) {
            this.fc.editReason.setValidators([Validators.required]);
            this.fc.editReason.updateValueAndValidity();
            this.dataChanged.set(true);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private saveDraftPrescription() {
    if (this.personalDoctor()) return this.updateDraftPrescription();
    else return this.createDraftPrescription();
  }

  private createDraftPrescription() {
    const formValue = {
      ...this.personalDoctorForm.getRawValue(),
      id: crypto.randomUUID(),
      guaranteeId: this.guaranteeApplicationId() || null,
    };

    const combinedReason = combineReasons({
      editReason: formValue.editReason,
      statusReason: formValue.statusReason,
    });

    this.store.dispatch(addPrescriptionDraft({ draft: { ...formValue, reason: combinedReason } }));
  }

  private updateDraftPrescription() {
    const formValue = { ...this.personalDoctorForm.getRawValue(), id: this.personalDoctor().id, guaranteeId: this.guaranteeApplicationId() || null };
    const combinedReason = combineReasons({
      editReason: formValue.editReason,
      statusReason: formValue.statusReason,
    });

    this.store.dispatch(updatePrescriptionDraft({ draft: { ...formValue, reason: combinedReason } }));
  }

  private savePrescriptionForm() {
    return this.personalDoctor() ? this.updatePrescription() : this.createPrescription();
  }

  private createPrescription() {
    const formValue = this.personalDoctorForm.getRawValue();
    const editReason = this.completionChecked() ? this.personalDoctorForm.getRawValue().completionReason : '';
    const combinedReason = combineReasons({
      editReason: editReason,
      statusReason: formValue.statusReason,
    });

    return this.prescriptionsService.createPrescription(this.memberId(), this.guaranteeApplicationId(), this.adaptFormValueToRequestBody(formValue, combinedReason));
  }

  private updatePrescription() {
    const formValue = this.personalDoctorForm.getRawValue();
    let editReason: string;

    if (this.completionChecked() && !formValue.editReason) editReason = formValue.completionReason;
    else editReason = formValue.editReason;

    const combinedReason = combineReasons({
      editReason: editReason,
      statusReason: formValue.statusReason,
    });

    return this.prescriptionsService.updatePrescription(
      this.memberId(),
      this.guaranteeApplicationId(),
      this.personalDoctor()?.id,
      this.adaptFormValueToRequestBody(formValue, combinedReason)
    );
  }

  private adaptFormValueToRequestBody(formValue: typeof this.personalDoctorForm.value, combinedReason: string) {
    const updatedDateTime = new Date(formValue.endDate);

    updatedDateTime.setHours(23, 59, 59, 999);

    return <PrescriptionItem<PersonalDoctorMetadata>>{
      id: this.personalDoctor() ? this.personalDoctor().id : crypto.randomUUID(),
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: combinedReason,
      metadata: {
        service_id: formValue.metadata.serviceId,
        doctor_id: formValue.metadata.doctorId,
        doctor_partner_id: formValue.metadata.doctorPartnerId ?? null,
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      originalPrescriptionId: formValue.originalPrescriptionId,
    };
  }

  protected getEndDate(prescriptionQuantity: number = 1): Date {
    const daysAdded: number = DOCTOR_MIN_PERIOD * prescriptionQuantity;
    const validStartDate = this.fc ? this.fc.startDate.value : this.startDate;

    this.endDate = new Date(validStartDate); // Reset date to start date
    this.endDate.setDate(this.endDate.getDate() + daysAdded); // Add days
    this.endDate.setHours(23, 59, 59, 999); // Set time to end of the day
    return this.endDate;
  }

  protected submitPrescription(): void {
    this.personalDoctorForm.markAllAsTouched();
    this.personalDoctorForm.updateValueAndValidity();

    if (this.personalDoctorForm.valid && (this.statusChanged() || this.dataChanged() || this.completionChecked())) {
      this.loading.set(true);
      this.savePrescriptionForm()
        .pipe(take(1))
        .subscribe({
          next: result => this.dialogRef.close(result),
          error: error => {
            console.error('Error:', error);
            this.loading.set(false);
          },
        });
    }
  }

  protected submitDraft(): void {
    this.personalDoctorForm.markAllAsTouched();
    this.personalDoctorForm.updateValueAndValidity();

    if (this.personalDoctorForm.valid && (this.statusChanged() || this.dataChanged())) {
      this.loading.set(true);
      this.saveDraftPrescription();
      this.closeDialog();
    }
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }
}
